import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  TrendingUp as TrendingUpIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { whatsappStatsService, WhatsAppStats as WhatsAppStatsData, WhatsAppMessageLog } from '../../services/whatsappStatsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`whatsapp-stats-tabpanel-${index}`}
      aria-labelledby={`whatsapp-stats-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const WhatsAppStats: React.FC = () => {
  const [stats, setStats] = useState<WhatsAppStatsData | null>(null);
  const [logs, setLogs] = useState<WhatsAppMessageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, logsData] = await Promise.all([
        whatsappStatsService.getStats(),
        whatsappStatsService.getRecentLogs(20)
      ]);

      setStats(statsData);
      setLogs(logsData);
    } catch (err) {
      console.error('Error loading WhatsApp stats:', err);
      setError('فشل في تحميل إحصائيات الرسائل');
    } finally {
      setLoading(false);
    }
  };

  const getMessageTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      deviceReceived: 'استلام الجهاز',
      deviceInProgress: 'أثناء العمل',
      deviceCompleted: 'اكتمال الإصلاح',
      deviceUrgent: 'تذكير عاجل'
    };
    return labels[type] || type;
  };

  const getMessageTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' => {
    const colors: Record<string, 'primary' | 'secondary' | 'success' | 'warning'> = {
      deviceReceived: 'primary',
      deviceInProgress: 'secondary',
      deviceCompleted: 'success',
      deviceUrgent: 'warning'
    };
    return colors[type] || 'primary';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              جاري تحميل الإحصائيات...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <IconButton color="inherit" size="small" onClick={loadData}>
          <RefreshIcon />
        </IconButton>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          📊 إحصائيات رسائل WhatsApp
        </Typography>
        <Tooltip title="تحديث">
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <WhatsAppIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    إجمالي الرسائل
                  </Typography>
                </Box>
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.totalSent.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <TodayIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    اليوم
                  </Typography>
                </Box>
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {stats.sentToday.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <WeekIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    هذا الأسبوع
                  </Typography>
                </Box>
                <Typography variant="h4" color="secondary.main" fontWeight="bold">
                  {stats.sentThisWeek.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <MonthIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    هذا الشهر
                  </Typography>
                </Box>
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {stats.sentThisMonth.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            aria-label="WhatsApp stats tabs"
          >
            <Tab label="حسب النوع" id="whatsapp-stats-tab-0" />
            <Tab label="السجلات الأخيرة" id="whatsapp-stats-tab-1" />
          </Tabs>
        </Box>

        {/* By Type Tab */}
        <TabPanel value={activeTab} index={0}>
          {stats && (
            <Grid container spacing={2}>
              {Object.entries(stats.sentByType).map(([type, count]) => (
                <Grid item xs={12} sm={6} md={3} key={type}>
                  <Box textAlign="center" p={2}>
                    <Chip
                      label={getMessageTypeLabel(type)}
                      color={getMessageTypeColor(type)}
                      sx={{ mb: 2, fontSize: '1rem', py: 2 }}
                    />
                    <Typography variant="h3" fontWeight="bold" color={`${getMessageTypeColor(type)}.main`}>
                      {count.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      رسالة
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Recent Logs Tab */}
        <TabPanel value={activeTab} index={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>الحالة</TableCell>
                  <TableCell>النوع</TableCell>
                  <TableCell>العميل</TableCell>
                  <TableCell>رقم الطلب</TableCell>
                  <TableCell>التاريخ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {log.success ? (
                        <Tooltip title="تم الإرسال بنجاح">
                          <SuccessIcon color="success" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={log.error || 'فشل الإرسال'}>
                          <ErrorIcon color="error" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getMessageTypeLabel(log.messageType)}
                        color={getMessageTypeColor(log.messageType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.customerName}</TableCell>
                    <TableCell>#{log.orderId}</TableCell>
                    <TableCell>
                      {log.sentAt.toLocaleDateString('ar-EG')} {log.sentAt.toLocaleTimeString('ar-EG')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {logs.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                لا توجد رسائل مسجلة حتى الآن
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default WhatsAppStats;
